import React, { useCallback, useState, type PropsWithChildren } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Text } from '@atlaskit/primitives/compiled';
import {
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
	UNSAFE_UpdateOnChange as SpotlightUpdateOnChange,
} from '@atlaskit/spotlight';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { CONFLUENCE_RECAP_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

const i18n = defineMessages({
	header: {
		id: 'confluence-recap.publish-spotlight.header',
		defaultMessage: 'Ready to share?',
		description: 'Header text for the ready to share spotlight',
	},
	body: {
		id: 'confluence-recap.publish-spotlight.body',
		defaultMessage: 'Click publish so your team can see what you accomplished together!',
		description: 'Body text for the ready to share spotlight',
	},
	primaryAction: {
		id: 'confluence-recap.publish-spotlight.primary-action',
		defaultMessage: 'Got it',
		description: 'Primary action button text for the ready to share spotlight',
	},
});

const styles = cssMap({
	// This is to override the "classic" `.wiki-content hxx` customized font size by editor (see those in next/packages/content-renderer/src/renderers/StyledRenderer.tsx)
	// The override will be applied to the heading in the spotlight to achieve the correct Heading size (xsmall = 14px)
	wikiContentOverride: {
		font: token('font.heading.xsmall'),
	},
});

export interface ConfluenceRecapPublishSpotlightProps extends PropsWithChildren {
	onDismiss?: () => void;
}

const ConfluenceRecapPublishSpotlightComponent = ({
	children,
	onDismiss,
}: ConfluenceRecapPublishSpotlightProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { PopoverContent } = useChoreographerAssets();
	const { setOnboardingState } = useSetOnboardingState();
	const [isDismissed, setIsDismissed] = useState(false);
	// PopoverContent requires client-side DOM measurements for proper positioning, e.g. getBoundingClientRect()
	// During SSR, positioning calculations fail and the spotlight appears in wrong position e.g. the top-left corner
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const handleSpotlightInteraction = useCallback(
		(action: string, actionSubject: string, actionSubjectIdSuffix?: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject,
					actionSubjectId: `confluenceRecapPublishSpotlight${actionSubjectIdSuffix ?? ''}`,
					source: 'confluenceRecapPublishSpotlight',
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handleOnClick = useCallback(() => {
		handleSpotlightInteraction('clicked', 'button', 'CTA');
		setIsDismissed(true);
		onDismiss?.();
	}, [handleSpotlightInteraction, onDismiss]);

	const handleOnDismiss = useCallback(() => {
		handleSpotlightInteraction('clicked', 'button', 'Dismiss');
		setIsDismissed(true);
		onDismiss?.();
	}, [handleSpotlightInteraction, onDismiss]);

	if (isDismissed) {
		return <>{children}</>;
	}

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<SpotlightUpdateOnChange />
			<PopoverContent
				messageId="confluence-recap-publish-spotlight"
				messageType="engagement"
				dismiss={handleOnDismiss}
				placement="bottom-end"
				offset={[-16, 11]}
				isVisible={!isDismissed && !renderServerPlaceholder}
				onMessageDisposition={(status) => {
					if (status === 'started') {
						createAnalyticsEvent({
							type: 'sendScreenEvent',
							data: {
								name: 'ConfluenceRecapPublishSpotlight',
								attributes: {
									source: 'PublishButton',
								},
							},
						}).fire();

						// Mark spotlight as seen with current timestamp
						setOnboardingState({
							key: CONFLUENCE_RECAP_ONBOARDING_STATE_KEYS.HAS_SEEN_PUBLISH_SPOTLIGHT,
							value: new Date().toISOString(),
						});

						addUFOCustomData({
							confluenceRecapSpotlightDisplayed: true,
						});
					}
				}}
			>
				<SpotlightCard testId="confluence-recap-publish-spotlight">
					<SpotlightHeader>
						<SpotlightHeadline>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<span css={styles.wikiContentOverride}>
								<FormattedMessage {...i18n.header} />
							</span>
						</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={handleOnDismiss} autoFocus={false} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>
						<Text>
							<FormattedMessage {...i18n.body} />
						</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={handleOnClick}>
								<FormattedMessage {...i18n.primaryAction} />
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};

export const ConfluenceRecapPublishSpotlight = (props: ConfluenceRecapPublishSpotlightProps) => (
	<TransparentErrorBoundary attribution={Attribution.FLYWHEEL_ACTIVATION}>
		<ConfluenceRecapPublishSpotlightComponent {...props} />
	</TransparentErrorBoundary>
);
